import React, { useEffect, useRef, useState } from "react";
import LabbaikSlide from "../slideshow/LabbaikSlide";
import { HomePoster, PosterColours } from "./homePosters";

interface PosterViewerProps {
  posters: HomePoster[];
  colours: PosterColours;
  seen: string[];
  onSeen: (id: string) => void;
  onClose: () => void;
}

const SWIPE_MIN_PX = 60;

// The masjid's posters one at a time, only when asked for. Nothing moves until the household does.
const PosterViewer: React.FC<PosterViewerProps> = ({ posters, colours, seen, onSeen, onClose }) => {
  // Opens at the first poster this tablet hasn't shown yet.
  const [index, setIndex] = useState(() =>
    Math.max(0, posters.findIndex((poster) => !seen.includes(poster.id))),
  );
  const swipeStartX = useRef<number | null>(null);
  const count = posters.length;
  // The hourly refresh can drop a poster while this is open.
  const shown = index % count;
  const poster = posters[shown];
  const backdrop = poster.labbaik ? poster.labbaik.imageUrl : poster.url;
  const step = (by: number) => setIndex((i) => (i + by + count) % count);

  useEffect(() => {
    onSeen(poster.id);
  }, [poster.id, onSeen]);

  // A TV remote's left and right.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      // The bar stays usable under the posters, and keeps its own left and right.
      if ((e.target as Element).closest?.(".hs-bar")) return;
      // Not also a focus move: up and down still reach the buttons.
      e.preventDefault();
      setIndex((i) => (i + (e.key === "ArrowLeft" ? count - 1 : 1)) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count]);

  return (
    <div
      className="hs-posters"
      role="dialog"
      aria-label="Posters"
      onPointerDown={(e) => (swipeStartX.current = e.clientX)}
      onPointerUp={(e) => {
        const start = swipeStartX.current;
        swipeStartX.current = null;
        if (start === null || count < 2 || Math.abs(e.clientX - start) < SWIPE_MIN_PX) return;
        step(e.clientX < start ? 1 : -1);
      }}
    >
      {backdrop && <img className="hs-posters-blur" src={backdrop} alt="" aria-hidden="true" />}
      {poster.labbaik ? (
        <div className="hs-posters-labbaik">
          <LabbaikSlide item={poster.labbaik} colours={colours} />
        </div>
      ) : (
        <img className="hs-posters-image" src={poster.url} alt={poster.title} draggable={false} />
      )}
      {count > 1 && (
        <>
          <button
            className="hs-posters-arrow hs-posters-prev"
            onClick={() => step(-1)}
            aria-label="Previous poster"
          >
            ‹
          </button>
          <button
            className="hs-posters-arrow hs-posters-next"
            onClick={() => step(1)}
            aria-label="Next poster"
          >
            ›
          </button>
          <span className="hs-posters-count">
            {shown + 1} of {count}
          </span>
        </>
      )}
      <button className="hs-close hs-posters-close" onClick={onClose} aria-label="Close posters">
        ✕
      </button>
    </div>
  );
};

export default PosterViewer;
